import { useState } from 'react';
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  Trash2,
  Check,
  X,
  Star,
  ClipboardCheck,
  ClipboardList,
  Loader2,
  RotateCcw,
  ScanText,
} from 'lucide-react';
import type { SavedJob } from '@/lib/types';
import type { AssessmentSummary, AssessmentSummaryItem, Dimension, JDExtraction } from '@/lib/assessment';
import { averageScore, scoreColor, DIMENSION_META } from '@/lib/assessment';
import { useJobAssessment } from '@/hooks/useJobAssessment';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AssessmentPanel } from '@/components/assessment/AssessmentPanel';
import { JDBreakdown } from '@/components/assessment/JDBreakdown';
import { BackToListButton } from '@/components/BackToListButton';

type Props = {
  job: SavedJob;
  summary?: AssessmentSummaryItem;
  onBack: () => void;
  onDelete: (id: string) => void | Promise<void>;
  onToggleStar: (job: SavedJob) => void;
  onAssessmentChange?: () => void;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function JobDetail({ job, summary, onBack, onDelete, onToggleStar, onAssessmentChange }: Props) {
  // The assessment state machine lives HERE (not inside AssessmentPanel) so it
  // survives detail↔panel navigation: a quiz generation kicked off in the panel
  // keeps running if the user pops back to the detail view, and the button
  // below reflects the in-flight state instead of offering a duplicate
  // "Generate" that would race the first one.
  const assessment = useJobAssessment(job.id);
  const [assessing, setAssessing] = useState(false);
  // Inline two-step confirm for delete: avoids modal dialogs and accidental
  // deletes. First click reveals confirm/cancel buttons; the second deletes.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // JD breakdown consumes 1 generation, so we gate it behind a two-step
  // confirm that surfaces the cost before running.
  const [confirmingBreakdown, setConfirmingBreakdown] = useState(false);

  async function confirmDelete() {
    setConfirmingDelete(false);
    await onDelete(job.id);
    onBack();
  }

  function exitAssessment() {
    setAssessing(false);
    // Re-sync the list-level summary so score badges reflect any changes.
    onAssessmentChange?.();
  }

  if (assessing) {
    return (
      <AssessmentPanel
        job={job}
        assessment={assessment}
        onBack={exitAssessment}
        onHome={onBack}
      />
    );
  }

  // Prefer the live hook data (freshest for this job); fall back to the
  // Dashboard summary while the hook's first probe is in flight.
  // Derived from the generating FLAGS (not phase) so a background generation
  // still disables conflicting actions even if the user navigated away from
  // the loading screen.
  const breakdownLoading = assessment.breakdownGenerating;
  const quizLoading = assessment.quizGenerating;
  const generating = breakdownLoading || quizLoading;
  const latest: AssessmentSummary | null = assessment.history[0] ?? summary?.latest ?? null;
  const hasBreakdown = !!assessment.breakdown || !!summary?.extraction;
  const hasQuiz = !!assessment.quiz;
  // Self-assessment entry is gated: the user must have a breakdown OR an
  // existing quiz (legacy). Without either, the button is disabled and shows
  // a hint to break down the JD first.
  const canAssess = hasBreakdown || hasQuiz;
  // Extraction: prefer the standalone breakdown, then the quiz's embedded
  // copy (legacy), then the dashboard summary.
  const extraction: JDExtraction | null =
    assessment.breakdown ?? assessment.quiz?.extraction ?? summary?.extraction ?? null;
  const attempts = assessment.history.length || summary?.attempts || 0;
  // Quota is counted in half-generation units server-side, so the remaining
  // number can be fractional (e.g. 9.5). A full breakdown costs 1.0; the user
  // needs >= 1.0 remaining to start one.
  const quotaExhausted = assessment.generationsRemaining !== null && assessment.generationsRemaining < 1;

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-card/95 px-2 py-2 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleStar(job)}
            aria-label={job.starred ? 'Unstar' : 'Star'}
            aria-pressed={job.starred}
            className={cn(
              job.starred
                ? 'text-amber-500 hover:text-amber-600'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Star className="h-4 w-4" fill={job.starred ? 'currentColor' : 'none'} />
          </Button>
          {confirmingDelete ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                title="Cancel"
                aria-label="Cancel remove"
                onClick={() => setConfirmingDelete(false)}
                className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title="Confirm remove"
                aria-label="Confirm remove saved job"
                onClick={() => void confirmDelete()}
                className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                autoFocus
              >
                <Check className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              className="gap-1 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 px-4 pt-4 pb-24">
        {job.source ? (
          <span className="mb-2 inline-block rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-secondary-foreground">
            {job.source}
          </span>
        ) : null}

        <h1 className="text-xl font-semibold leading-snug tracking-tight">
          {job.title}
        </h1>

        {job.advertiser ? (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{job.advertiser}</span>
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={job.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <ExternalLink className="h-4 w-4" /> View original listing
          </a>

          {/* JD Breakdown — one-shot analysis of the JD into the three funnel
              aspects. Consumes 1 generation. Once a breakdown exists it is NOT
              re-offered: the extraction is reusable, and re-running would only
              burn quota for the same input. Gated behind a two-step confirm so
              the cost is surfaced before running. */}
          {!hasBreakdown &&
            (breakdownLoading ? (
              <Button variant="default" disabled className="gap-1.5">
                <Loader2 className="h-4 w-4 animate-spin" /> Breaking down…
              </Button>
            ) : confirmingBreakdown ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600">
                  <ScanText className="h-3.5 w-3.5" /> Uses 1 generation
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Cancel"
                  aria-label="Cancel breakdown"
                  onClick={() => setConfirmingBreakdown(false)}
                  className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Confirm"
                  aria-label="Confirm breakdown"
                  onClick={() => {
                    setConfirmingBreakdown(false);
                    void assessment.generateBreakdown();
                  }}
                  className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-600"
                  autoFocus
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                onClick={() => setConfirmingBreakdown(true)}
                disabled={generating || quotaExhausted}
                className="gap-1.5"
              >
                <ScanText className="h-4 w-4" /> Break down JD
              </Button>
            ))}

          {/* Self-assessment entry — only shown once a breakdown (or legacy
              quiz) exists, so it's never presented in a disabled state. When
              an assessment already exists, the LatestResultCard below renders
              its own "Retake assessment" button, so we suppress this duplicate
              entry to avoid two retake buttons on screen. */}
          {!latest && canAssess ? (
            <Button
              variant="outline"
              onClick={() => setAssessing(true)}
              disabled={generating}
              className="gap-1.5"
            >
              {quizLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ClipboardCheck className="h-4 w-4" />
              )}
              {quizLoading ? 'Generating…' : 'Self-assess'}
            </Button>
          ) : null}
        </div>

        {/* Hint when self-assessment is locked */}
        {!canAssess ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Break down the JD first to unlock the self-assessment.
          </p>
        ) : null}

        {/* Quota counter */}
        {assessment.generationsRemaining !== null && (
          <p className="mt-1 text-xs text-muted-foreground">
            {assessment.generationsRemaining <= 0
              ? 'You\u2019ve used all 10 generations.'
              : `${assessment.generationsRemaining} of 10 generation${assessment.generationsRemaining === 1 ? '' : 's'} remaining`}
          </p>
        )}

        {/* In-flight banner — reflects whichever generation stage is running. */}
        {generating ? (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {breakdownLoading
              ? 'Breaking down the JD — extracting the three key aspects. This takes ~30s.'
              : 'Generating your self-assessment quiz — this takes ~2min. Feel free to come back; it keeps running.'}
          </div>
        ) : null}

        {/* Latest self-assessment result (if any) */}
        {latest ? (
          <LatestResultCard
            latest={latest}
            attempts={attempts}
            onRetake={() => setAssessing(true)}
            timeAgoStr={timeAgo(latest.createdAt)}
          />
        ) : null}

        {/* The three JD aspects extracted at generation time — the basis for each
            dimension's questions, and a cleaner lens on the job than the raw JD. */}
        {extraction ? <JDBreakdown extraction={extraction} /> : null}

        <dl className="mt-4 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
          <div>
            <dt className="font-medium uppercase tracking-wide">Saved</dt>
            <dd className="mt-0.5 text-foreground">{formatDate(job.createdAt)}</dd>
          </div>
          {job.updatedAt !== job.createdAt ? (
            <div>
              <dt className="font-medium uppercase tracking-wide">Updated</dt>
              <dd className="mt-0.5 text-foreground">{formatDate(job.updatedAt)}</dd>
            </div>
          ) : null}
        </dl>

        <div className="mt-5">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Description
          </h2>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {job.description}
            </p>
          </div>
        </div>

        <BackToListButton onHome={onBack} />
      </div>
    </div>
  );
}

function LatestResultCard({
  latest,
  attempts,
  onRetake,
  timeAgoStr,
}: {
  latest: AssessmentSummary;
  attempts: number;
  onRetake: () => void;
  timeAgoStr: string;
}) {
  const avg = averageScore(latest);
  const dims: Array<{
    key: keyof typeof DIMENSION_META;
    score: number;
    level: string;
  }> = (latest.dimensions ?? (['willingness', 'hard_requirements', 'soft_requirements'] as Dimension[])).map(
    (dim) => ({
      key: dim,
      score:
        dim === 'willingness'
          ? latest.willingnessScore.score
          : dim === 'hard_requirements'
            ? latest.hardScore.score
            : latest.softScore.score,
      level:
        dim === 'willingness'
          ? latest.willingnessScore.level
          : dim === 'hard_requirements'
            ? latest.hardScore.level
            : latest.softScore.level,
    }),
  );

  return (
    <div className="mt-4 rounded-xl border border-border bg-gradient-to-b from-card to-card/60 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" /> Latest self-assessment
        </div>
        <span className="text-[11px] text-muted-foreground">
          {attempts > 1 ? `${attempts} attempts · ` : ''}
          {timeAgoStr}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <div className="text-center">
          <p className={cn('text-3xl font-bold tabular-nums leading-none', scoreColor(avg))}>{avg}</p>
          <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">avg / 100</p>
        </div>
        <div className="h-10 w-px bg-border" />
        <div
          className={cn(
            'grid flex-1 gap-2',
            dims.length === 1 ? 'grid-cols-1' : dims.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
          )}
        >
          {dims.map((d) => {
            const Icon = DIMENSION_META[d.key].icon;
            return (
              <div key={d.key} className="text-center">
                <Icon className="mx-auto h-4 w-4 text-primary" strokeWidth={2} />
                <p className={cn('mt-0.5 text-base font-semibold tabular-nums', scoreColor(d.score))}>
                  {d.score}
                </p>
                <p className="truncate text-[10px] text-muted-foreground" title={d.level}>
                  {DIMENSION_META[d.key].short}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <Button variant="outline" size="sm" onClick={onRetake} className="mt-3 w-full gap-1.5">
        <RotateCcw className="h-4 w-4" /> Retake assessment
      </Button>
    </div>
  );
}
